import Image from "next/image";
import Link from "next/link";
import { useFormContext } from "react-hook-form";
import { EventBySlugOutput } from "~/types/EventTypesInferred";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "~/components/v3/shadcn/Card";
import { useEventDetailsPageStore } from "~/state/store";
import { If } from "../Conditional";
import { RiAddCircleLine, RiLink } from "react-icons/ri";
import { cn } from "~/utils/fontsConfig";
import { Input } from "../themed/Input";
import { Label } from "../shadcn/Label";
import { TextArea } from "../themed/TextArea";
import { Button } from "../themed/Button";
import { BiTrash } from "react-icons/bi";
import { RouterOutputs } from "~/utils/api";
import { useRouter } from "next/navigation";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "~/components/shadcn/Popover";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
} from "~/components/shadcn/Command";
import { RxCheck } from "react-icons/rx";
import { useCallback, useEffect, useState } from "react";
import { searchSponsors } from "~/app/events/[slug]/sponsor/actions";
import { env } from "~/env.mjs";
import { Prisma } from "@prisma/client";
import { useSession } from "next-auth/react";
import SelectSponsorshipPackageModal from "~/app/community-management/[communitySlug]/sponsors/packages/_components/SelectSponsorshipPackageModal";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "~/components/shadcn/DropDownMenu";
import { LuMoreVertical } from "react-icons/lu";

type SponsorDetails = Prisma.SponsorDetailsGetPayload<{
    select: {
        id: true;
        slug: true;
        sponsorshipType: true;
        name: true;
        description: true;
        socials: true;
        image: true;
        url: true;
        eventBlurb: true;
    };
}>;

type Props = {
    event: EventBySlugOutput;
    permissions?: RouterOutputs["permissions"]["getUserPermissions"];
};

const EventSponsor = ({ event, permissions }: Props) => {
    const { data: session } = useSession();
    const router = useRouter();
    const form = useFormContext();
    let { panelMode } = useEventDetailsPageStore();

    if (!permissions?.isManager) panelMode = "default";

    if (event?.community?.hideSponsorships) {
        return null;
    }

    const [sponsors, setSponsors] = useState<SponsorDetails[]>(
        event.sponsorsDetails,
    );
    const [searchedSponsors, setSearchedSponsors] = useState<SponsorDetails[]>(
        [],
    );
    const [showPackageSelectionModal, setShowPackageSelectionModal] =
        useState(false);

    const handleSetSponsors = (sponsors: SponsorDetails[]) => {
        // update the form
        form.setValue(
            "sponsors",
            sponsors.map((sponsor) => sponsor.id),
            { shouldDirty: true },
        );
        // update the ui
        setSponsors(sponsors);
    };
    const handleSearchSponsor = async (value: string) => {
        // TODO could add debounce - 100ms
        const sponsors = await searchSponsors({
            search: value,
            limit: 3,
            communityId: event.communityId || "",
        });
        setSearchedSponsors(sponsors);
    };
    const handleRemove = useCallback(
        (id: string) => {
            const newSponsors = sponsors.filter((sponsor) => sponsor.id !== id);
            form.setValue(
                "sponsors",
                newSponsors.map((sponsor) => sponsor.id),
                {
                    shouldDirty: true,
                },
            );
            setSponsors(newSponsors);
        },
        [sponsors],
    );

    if (!event.isApplyToSponsorAllowed) return null;

    return (
        <>
            {sponsors.length == 0 && panelMode !== "edit" && (
                <Card className="relative overflow-hidden">
                    <div className="absolute inset-0 z-0 bg-gradient-to-br from-[#0F3B3B] to-[#1E3A8A]"></div>
                    <CardHeader className="relative z-10 flex flex-row items-center p-5">
                        <CardTitle className="grow text-white">
                            Sponsor this event
                        </CardTitle>
                    </CardHeader>
                    <If condition={session?.user?.role === "ADMIN"}>
                        <>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <div className="absolute right-2 top-2 z-10 flex items-center justify-center rounded-md bg-[#373A41] p-[5px]">
                                        <LuMoreVertical className="h-4 w-4 text-white" />
                                        <span className="sr-only">More</span>
                                    </div>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                        onClick={() =>
                                            setShowPackageSelectionModal(true)
                                        }
                                    >
                                        Select Package XadminX
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                            <SelectSponsorshipPackageModal
                                isOpen={showPackageSelectionModal}
                                setIsOpen={setShowPackageSelectionModal}
                                communityId={event.community?.id || ""}
                                event={event}
                                onSelectPackage={() => {}}
                            />
                        </>
                    </If>
                    <CardContent className="relative z-10 grid w-full grid-cols-1 p-5 pt-0">
                        <p className="text-N-200 mb-4">
                            Want to boost the experience of this event for
                            attendees while sharing your brand?
                        </p>
                        <Link
                            href={`https://2i6voyc6xd3.typeform.com/to/VWiekNjM#community_id=${event.communityId}&event_id=${event.id}&event_name=${event.name}&community_name=${event.community?.name}&community_slug=${event.community?.slug}`}
                            target="_blank"
                        >
                            <p className="text-secondary">
                                Learn more about sponsoring this event
                            </p>
                        </Link>
                    </CardContent>
                </Card>
            )}
            {(sponsors.length > 0 || panelMode === "edit") && (
                <Card>
                    <If condition={panelMode === "edit"}>
                        {" "}
                        <CardHeader>
                            <CardTitle className="flex flex-row">
                                <div className="flex-grow">Sponsor</div>
                                <FacetedFilterSelector
                                    placeholder="Select sponsor"
                                    onFilterChange={handleSearchSponsor}
                                    onSelectonChange={handleSetSponsors}
                                    options={searchedSponsors}
                                    accessor={(sd: SponsorDetails) => {
                                        return sd.slug;
                                    }}
                                />
                            </CardTitle>
                        </CardHeader>
                    </If>
                    <CardContent className="grid w-full grid-cols-1 divide-y divide-dotted divide-gray-800">
                        {sponsors.map((sponsor) => (
                            <div key={sponsor.id}>
                                <div className="mt-4 flex-grow text-lg">
                                    {sponsor.sponsorshipType}
                                </div>
                                <div className="flex w-full flex-row items-center py-4 first:pt-0 last:pb-0">
                                    <div className="relative mt-2 h-11 w-11 flex-shrink-0 self-start overflow-hidden rounded-full bg-gray-200">
                                        <Image
                                            src={
                                                `${env.NEXT_PUBLIC_ASSETS_URL}/community/${event.community?.slug}/sponsors/${sponsor.image}` ||
                                                "/icons/your_logo.svg"
                                            }
                                            alt={`sponsored by ${sponsor.name}`}
                                            width={60}
                                            height={60}
                                            layout="fixed"
                                            className="rounded-full"
                                        />
                                    </div>

                                    <div className="ml-4 flex grow flex-col">
                                        <div className="font-medium">
                                            <If
                                                condition={
                                                    panelMode === "default"
                                                }
                                            >
                                                {sponsor.description}
                                            </If>
                                            <If
                                                condition={panelMode === "edit"}
                                            >
                                                <div className="absolute right-6">
                                                    <Button
                                                        onClick={() =>
                                                            handleRemove(
                                                                sponsor.id,
                                                            )
                                                        }
                                                        variant="destructive"
                                                        size="xs"
                                                    >
                                                        <BiTrash />
                                                    </Button>
                                                </div>
                                                <Label>Description</Label>
                                                <TextArea
                                                    disabled={true}
                                                    value={sponsor.description}
                                                    className="h-[80px]"
                                                />
                                            </If>
                                        </div>

                                        <div className="mt-2">
                                            <If
                                                condition={
                                                    panelMode === "default"
                                                }
                                            >
                                                <Link
                                                    href={
                                                        sponsor.url ||
                                                        "https://www.getriver.io/sponsors"
                                                    }
                                                    className="group text-v3-primary"
                                                    target="_blank"
                                                >
                                                    <If
                                                        condition={
                                                            !!sponsor.name
                                                        }
                                                    >
                                                        Learn more about{" "}
                                                        <span className="underline-offset-4 group-hover:underline">
                                                            {sponsor.name}
                                                        </span>
                                                    </If>
                                                    <If
                                                        condition={
                                                            !sponsor.name
                                                        }
                                                    >
                                                        <Button
                                                            onClick={() => {
                                                                if (
                                                                    permissions?.isAdmin
                                                                ) {
                                                                    router.push(
                                                                        `/events/${event.slug}/sponsor`,
                                                                    );
                                                                }
                                                            }}
                                                        >
                                                            Apply
                                                        </Button>
                                                    </If>
                                                </Link>
                                            </If>
                                            <If
                                                condition={panelMode === "edit"}
                                            >
                                                <Label>Name</Label>
                                                <Input
                                                    disabled={true}
                                                    value={sponsor.name}
                                                />
                                                <Label>URL</Label>
                                                <Input
                                                    disabled={true}
                                                    value={sponsor.url}
                                                    prefixComponent={
                                                        <div className="flex flex-row items-center">
                                                            <RiLink className="text-v3-N-300" />
                                                        </div>
                                                    }
                                                    className={cn(
                                                        "w-full text-v3-primary",
                                                        !sponsor.url &&
                                                            "underline",
                                                    )}
                                                    placeholder="https://sponsorname.com/..."
                                                />
                                            </If>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}
        </>
    );
};

export const FacetedFilterSelector = <T extends { id: string }>({
    placeholder,
    accessor,
    onFilterChange,
    onSelectonChange,
    options,
}: {
    placeholder: string;
    accessor: (row: T) => string;
    onFilterChange: (value: string) => void;
    onSelectonChange: (values: T[]) => void;
    options: T[];
}) => {
    const [open, setOpen] = useState(false);
    const [selectedValues, setSelectedValues] = useState<T[]>([]);
    const [allOptions, setAllOptions] = useState<T[]>([]);

    useEffect(() => {
        if (!options.length) return;
        const allOptions = new Map();
        options.forEach((item) => allOptions.set(item.id, item));
        selectedValues.forEach((item) => allOptions.set(item.id, item));
        setAllOptions([...allOptions.values()]);
        onSelectonChange(selectedValues);
    }, [options, selectedValues]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    role="combobox"
                    aria-expanded={open}
                    variant="ghost"
                    className={`
                        border
                        border-v3-N-500
                        text-xs
                        hover:bg-v3-N-500
                    `}
                >
                    <RiAddCircleLine className="mr-1 h-4 w-4" />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="w-[330px] bg-primary p-0 md:w-[400px]"
                align="start"
            >
                <Command shouldFilter={false}>
                    <CommandInput
                        placeholder={placeholder}
                        onValueChange={onFilterChange}
                    />
                    <CommandEmpty>Nothing found.</CommandEmpty>
                    <CommandGroup className="max-h-[300px] overflow-y-auto">
                        {allOptions.map((option, index) => {
                            const value = accessor(option);
                            const isSelected = selectedValues.includes(option);
                            return (
                                <CommandItem
                                    key={index}
                                    onSelect={() => {
                                        setSelectedValues((vals) => {
                                            if (vals.includes(option))
                                                return vals.filter(
                                                    (e) => e !== option,
                                                );
                                            return [...vals, option];
                                        });
                                    }}
                                >
                                    <div
                                        className={cn(
                                            "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                            isSelected
                                                ? "bg-primary text-v3-N-300"
                                                : "opacity-50 [&_svg]:invisible",
                                        )}
                                    >
                                        <RxCheck className={cn("h-4 w-4")} />
                                    </div>
                                    <span>{value}</span>
                                </CommandItem>
                            );
                        })}
                    </CommandGroup>
                </Command>
            </PopoverContent>
        </Popover>
    );
};

export default EventSponsor;
